/* eslint-disable react-hooks/exhaustive-deps */
import clsx from 'clsx'
import axios, {CancelTokenSource} from 'axios'
import swal from 'sweetalert'
import {useHistory} from 'react-router'
import {useTranslation} from 'react-i18next'
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {useDispatch, connect} from 'react-redux'

import './header.css'
import Topbar from './Topbar'
import {useLayout} from '../../core'
import {KTSVG} from '../../../helpers'
import {AppState} from '../../../../setup/redux/Store'
import * as loginUser from '../../../../app/modules/auth/redux/AuthRedux'
import {BACKEND_UPLOAD_URL, BACKEND_API_URL, BACKEND_API_V2, removeAllSession} from '../../../../config/config'
import {useSubscriptionsContext} from '../../../../context/SubscriptionsContext'
import useDebounce from '~/setup/hooks/useDebounce'

interface Props {
  insepctorMode: any
  insepctorUserName: any
}

function HeaderWrapper({insepctorMode, insepctorUserName}: Props) {
  const subscriptions = useSubscriptionsContext()

  const {t} = useTranslation()
  const dispatch = useDispatch()
  const {classes, attributes} = useLayout()
  const [curInspectorMode, setCurInspectorMode] = useState(false)
  const [curInspectorUsername, setCurInspectorUsername] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)

  let tokenValue = localStorage.getItem('token')

  const history = useHistory()
  const [curSearch, setCurSearch] = useState('')
  const [curUserLists, setCurUserLists] = useState<Array<any>>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [isSearching, setIsSearching] = useState(false)

  const debouncedCurSearch = useDebounce(curSearch, 500)
  const cancelTokenRef = useRef<CancelTokenSource | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const [ddPos, setDdPos] = useState<{top: number; left: number; width: number} | null>(null)

  // Fixed-position the results dropdown (anchored to the search bar) so it escapes the
  // fixed header's stacking/clipping context — mirrors the working .show-user-toggleMenu.
  useLayoutEffect(() => {
    if (!showDropdown) return
    const compute = () => {
      const el = barRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      setDdPos({top: r.bottom + 4, left: r.left, width: r.width})
    }
    compute()
    // Re-measure on the next frame: on the first open after a page load the bar can be
    // measured before layout has fully settled, which otherwise leaves the dropdown
    // overlapping the input until the next search re-triggers this effect.
    const raf = requestAnimationFrame(compute)
    window.addEventListener('resize', compute)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', compute)
    }
  }, [showDropdown, curUserLists.length, isSearching, searchOpen])

  const inspectorStyle = {
    height: '80px',
    backgroundColor: '#009ef7',
  }
  const unInspectorStyle = {
    height: '80px',
    backgroundColor: 'white',
  }

  useEffect(() => {
    if (insepctorMode && insepctorMode === 1) {
      setCurInspectorMode(true)
      setCurInspectorUsername(insepctorUserName)
    }
  }, [insepctorMode])

  useEffect(() => {
    if (!debouncedCurSearch) {
      setCurUserLists([])
      setShowDropdown(false)
      return
    }

    let req = {search_text: debouncedCurSearch}

    const fetchData = async () => {
      try {
        if (cancelTokenRef.current) {
          cancelTokenRef.current.cancel('Operation canceled due to new request')
        }
        const newCancelToken = axios.CancelToken.source()
        cancelTokenRef.current = newCancelToken

        const response = await axios.post(`${BACKEND_API_V2}/inspector/filterUserList`, req, {
          headers: {'x-access-token': `${tokenValue}`},
          cancelToken: newCancelToken.token,
        })

        const {result, token, auth} = response.data

        if (auth === false) {
          removeAllSession()
          history.push('/auth/login')
        } else if (result === 'success') {
          const users =
            token?.length > 0
              ? token.map((item: any, index: number) => ({
                  index,
                  id: item.id,
                  username: item.user_name,
                  firstname: item.first_name,
                  secondname: item.second_name,
                  email: item.email,
                  photoUrl: item.photo_url,
                }))
              : []
          setCurUserLists(users)
          setIsSearching(false)
          setShowDropdown(true)
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          setIsSearching(false)
          console.error('Error fetching user data:', error)
        }
      }
    }

    fetchData()
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Component unmounted or search changed')
      }
    }
  }, [debouncedCurSearch])

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleDisableInspector = () => {
    setCurInspectorUsername('')
    setCurInspectorMode(false)

    dispatch(loginUser.actions.insepctorDisable(0))
  }

  const handleSearchUser = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value
    setCurSearch(val)
    if (val.length > 0) {
      setIsSearching(true)
      setShowDropdown(true)
    } else {
      setIsSearching(false)
      setShowDropdown(false)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setShowDropdown(false)
      setSearchOpen(false)
    }
  }

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setCurSearch('')
    setCurUserLists([])
    setShowDropdown(false)
  }

  const handleSelectUser = useCallback(
    async (userId: number, username: string) => {
      setShowDropdown(false)
      setCurSearch('')
      setCurUserLists([])
      setCurInspectorMode(true)
      setCurInspectorUsername(username)

      const req = {userId}
      try {
        const permissionData = await axios.post(`${BACKEND_API_URL}/inspector/getUserInfo`, req, {
          headers: {'x-access-token': `${tokenValue}`},
        })
        const {result, token, auth} = permissionData.data

        if (auth === false) {
          removeAllSession()
          history.push('/auth/login')
        } else if (result === 'success') {
          dispatch(
            loginUser.actions.inspectorUser(
              1,
              token?.user_name,
              token?.id,
              token?.email,
              token?.first_name,
              token?.second_name,
              token?.birth,
              token?.streetaddress,
              token?.suite,
              token?.postalcode,
              token?.cityaddress,
              token?.country_code || '',
              token?.country_id,
              token?.timezone,
              token?.photo_url,
              token?.cryptowallet === null || token?.cryptowallet === undefined
                ? ''
                : token?.cryptowallet,
              token?.tv_username === null || token?.tv_username === undefined
                ? ''
                : token?.tv_username,
              token?.whatsapp_number,
              token?.telegram_username
            )
          )
        }
      } catch (error) {
        swal({
          title: t('TOP_INSPECTORMODE_BTN_TEXT'),
          text: 'Failed to load user info',
          icon: 'error',
        })
        setCurInspectorMode(false)
        setCurInspectorUsername('')
      }
    },
    [tokenValue, dispatch, history, t]
  )

  // Shared search input+dropdown used in both desktop and mobile-open states
  const searchInputAndDropdown = (
    <div className='inspector-search-wrapper' ref={wrapperRef}>
      <div className='inspector-search-bar' ref={barRef}>
        <span className='inspector-search-label'>
          {t('TOP_INSPECTORMODE_BTN_TEXT')}
        </span>
        <span className='inspector-search-divider-icon'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
          >
            <rect
              opacity='0.5'
              x='17.0365'
              y='15.1223'
              width='8.15546'
              height='2'
              rx='1'
              transform='rotate(45 17.0365 15.1223)'
              fill='currentColor'
            />
            <path
              d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
              fill='currentColor'
            />
          </svg>
        </span>
        <input
          type='text'
          className='inspector-search-input'
          placeholder={t('TOP_INSPECTORMODEDLG_SEARCH_PLACEHOLADER')}
          value={curSearch}
          onChange={handleSearchUser}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (curUserLists.length > 0) setShowDropdown(true)
          }}
        />
      </div>
      <div className='position-relative'>
        {showDropdown && (
          <div
            className='inspector-dropdown'
            style={ddPos ? {position: 'fixed', top: ddPos.top, left: ddPos.left, width: ddPos.width, zIndex: 1001} : {visibility: 'hidden'}}
          >
            {isSearching ? (
              <div className='inspector-dropdown-empty'>
                <span className='spinner-border spinner-border-sm me-2' role='status' />
                Searching...
              </div>
            ) : curUserLists.length > 0 ? (
              curUserLists.map((item) => (
                <div
                  key={item.id}
                  className='inspector-dropdown-item'
                  onClick={() => handleSelectUser(item.id, item.username)}
                >
                  <div className='symbol symbol-35px symbol-circle'>
                    <img alt='Pic' src={BACKEND_UPLOAD_URL + 'nophoto.jpg'} />
                  </div>
                  <div className='inspector-dropdown-info'>
                    <div className='inspector-dropdown-name'>
                      {item.firstname + ' ' + item.secondname}
                    </div>
                    <div className='inspector-dropdown-email'>{item.email}</div>
                  </div>
                  <div className='inspector-dropdown-username'>{item.username}</div>
                </div>
              ))
            ) : (
              <div className='inspector-dropdown-empty'>No users found</div>
            )}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div
      id='kt_header'
      className={clsx('header', classes.header.join(' '), 'align-items-stretch')}
      {...attributes.headerMenu}
      style={curInspectorMode === true ? inspectorStyle : unInspectorStyle}
    >
      <div
        className={clsx(
          classes.headerContainer.join(' '),
          'd-flex align-items-stretch justify-content-between header-container-mobile'
        )}
      >
        {/* ── MOBILE: search-open overlay (hidden on lg+) ── */}
        {searchOpen && (
          <div className='d-flex d-lg-none align-items-center header-mobile-search-open'>
            {subscriptions.affiliate && searchInputAndDropdown}
            <button
              className='btn btn-icon ms-2 header-mobile-search-close'
              onClick={handleCloseSearch}
              aria-label='Suche schließen'
              style={{minWidth: 44, minHeight: 44}}
            >
              <span aria-hidden='true' style={{fontSize: '1.25rem', lineHeight: 1}}>&times;</span>
            </button>
          </div>
        )}

        {/* ── Normal header row (hidden on mobile when search is open) ── */}
        <div
          className={clsx(
            'd-flex align-items-stretch justify-content-between w-100',
            searchOpen ? 'd-none d-lg-flex' : 'd-flex'
          )}
        >
          {/* begin::Aside mobile toggle */}
          <div className='d-flex align-items-center ms-n3 me-1 flex-grow-1 min-w-0' title='Show aside menu'>
            <div
              className='btn btn-icon btn-hover-back show-sidebar w-30px h-30px w-md-40px h-md-40px flex-shrink-0'
              id='kt_aside_mobile_toggle'
            >
              {curInspectorMode === false ? (
                <KTSVG
                  path='/media/icons/duotune/abstract/abs015.svg'
                  className='svg-icon-2x mt-1'
                />
              ) : (
                <img
                  src='/media/icons/duotune/abstract/abs015_dark.png'
                  alt='sidebar_avatar'
                  className='w-30px h-30px w-md-40px h-md-40px'
                />
              )}
            </div>

            {subscriptions.affiliate &&
              (curInspectorUsername.length > 0 ? (
                <div className='ms-3 leave-inspectormode align-items-center'>
                  <button
                    className='btn btn-light-primary inspector-btn align-self-center me-3 p-3'
                    onClick={handleDisableInspector}
                  >
                    {t('TOP_UNINSPECTORMODE_BTN_TEXT')}
                  </button>
                  <div className='d-flex justify-content-center'>
                    <span className='fs-5 fw-bolder text-light text-hover-primary text-center'>
                      {t('TOP_INSPECTORMODE_TEXT1')}&nbsp;{curInspectorUsername}
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  {/* Desktop: full inline search (lg+) */}
                  <div className='d-none d-lg-block flex-grow-1 min-w-0'>
                    {searchInputAndDropdown}
                  </div>

                  {/* Mobile: search icon button (below lg, not in search-open mode) */}
                  <button
                    className='d-flex d-lg-none btn btn-icon ms-2 header-mobile-search-icon'
                    onClick={() => setSearchOpen(true)}
                    aria-label='Suche öffnen'
                    style={{minWidth: 44, minHeight: 44}}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='20'
                      height='20'
                      viewBox='0 0 24 24'
                      fill='none'
                      aria-hidden='true'
                    >
                      <rect
                        opacity='0.5'
                        x='17.0365'
                        y='15.1223'
                        width='8.15546'
                        height='2'
                        rx='1'
                        transform='rotate(45 17.0365 15.1223)'
                        fill='currentColor'
                      />
                      <path
                        d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                        fill='#009ef7'
                      />
                    </svg>
                  </button>
                </>
              ))}
          </div>

          {/* begin::Wrapper (Topbar: language + avatar) */}
          <div
            className='d-flex align-items-stretch justify-content-between'
            style={{position: 'fixed', top: '18px', right: '10px'}}
          >
            <div className='d-flex align-items-stretch'>
              <Topbar curInspectorMode={curInspectorMode} searchOpen={searchOpen} />
            </div>
          </div>
          {/* end::Wrapper */}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: AppState) => ({
  insepctorMode: state.auth.inspectorMode,
  insepctorUserName: state.auth.inspector_user,
})

export default connect(mapStateToProps, {})(HeaderWrapper)
